import { BlogPostContentType } from 'constants/blog';
import {
  BLOG_COMPOUNDING_HABITS_101,
  BLOG_COMPOUNDING_HABITS_MARGINAL_GAINS,
} from 'constants/images';
import { BlogPost } from 'containers/blog/post/types';

const CompoundingHabits: BlogPost = {
  id: 'compounding-habits',
  title: 'Compounding habits',
  subtitle: 'Doing the little things time and again',
  previewImage: BLOG_COMPOUNDING_HABITS_101,
  date: new Date('1 Nov 2020'),
  content: [
    {
      type: BlogPostContentType.TITLE,
      content: 'Compounding habits',
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: 'Doing the little things time and again',
    },
    {
      type: BlogPostContentType.POST_INFO,
      content: '1 Nov 2020 · 7 minute read',
    },
    {
      type: BlogPostContentType.FULL_IMAGE,
      content: BLOG_COMPOUNDING_HABITS_101,
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'Studying 14 hours a day, too tired to understand. Going through a 5 hour gym session once in a while, no instant gain. Doing 100 pushups every other week, the body hurts. Running a marathon a month, coming back tired. Eating a bar of chocolate, instant pleasure. Then this must be the way to go.',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "Although this works in the short run, I don't see myself going too far with this approach. In the last while especially, the compounding effect is showing up each and every day, pointing out how slow growth wins in the long term. It's the small things done repeatedly that make me better, the constant hustle to become more and the consistency of doing things how they should be done. This sounds very idealistic and I'm not nearly close to that, but I have a strong feeling of heading in the right direction. It just takes time.",
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_COMPOUNDING_HABITS_MARGINAL_GAINS,
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'You\'re probably used to that example that states that staying the same every day, 1, multiplied over 365 days is still 1, while a small improvement every day, let\'s say 1.01, compounded over the same period results in a 37 fold increase, clearly showing that consistency, given enough time, brings great results. The same goes in the everyday life. Waking up, doing the work, especially the little things that are so easy to forget and staying consistent might not bring any results in one week or even one month, but at some point, the benefits will start showing up. After listening to the book of Darren Hardy, "The compound effect", I got once again the confirmation that everything actually makes sense. It\'s not the rare big leaps but the consistent baby steps that bring success at the end of the day. He clearly explains how keeping track of your habits, doing active course correction and setting clear goals will "jumpstart your income, your life, your success".',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'This book, like any other, is just the "how", but the evermore important are the "whats" and "whys" of actually applying the science in our lives. What are we doing every day to reach our goals? Why are we committed to do the little things? Without a clear direction, individual actions might seem fuzzy, with no clear outcome or goal. Waking up when it\'s still dark to study or going on a run when you could have done something more entertaining require a higher purpose that would keep people motivated. Finding that few end goals to which everything boils down to had a very big impact on my daily motivation. Knowing that each and every action I take is linked to a purpose gives me what I need to get started, instead of running endlessly through my daily routine.',
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content:
        'https://images.unsplash.com/photo-1567676702096-26615d982d9d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=100',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'Compounding does not only limit to personal life and the actions one takes for his goals but to a variety of other things. Have you ever went on a hike and saw the tall trees pointing at the sky? It took them many years to grow that tall while building deep and strong roots in the process. Probably you\'ve heard the term "compound interest", used in banking when small interest rates multiply each month to lead to a decent passive income in the long term.',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'Obviously, both the tree and the money in your account can be instantly influenced by outside actions, someone could either cut the tree or withdraw the money and all of a sudden the compounding comes to a halt. But do you remember that train ride, when it took ages until the train got moving? But once it got to speed it was unstoppable. It just took time to build momentum.',
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        "If someone would ask me what is the one thing that is hard to find, hard to build and easy to lose, I would definitely answer that it must be ... momentum, of course. This is highly linked to what many people call flow. It's that state when you feel very focused, very into a problem, moving on with ease and at an increasing pace. When you just work for hours and feel like nothing could stop you, or go on a run and after a certain point, the body just gets used to the pace. Those are great moments when the momentum is there and boosts your motivation. Then, what's hard? Building it, obviously. Although I got to know the things that work and that don't for myself, getting into a flow state and building momentum is still not 100% achievable through a well known path. Thus, I'm actively trying to figure out a clear recipe for getting to them.",
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content:
        'https://images.unsplash.com/photo-1469440017877-5a52ab9acdb0?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1000&q=100',
    },

    {
      type: BlogPostContentType.TEXT,
      content:
        "Someone asked me once what would be the superpower I would choose if I ever got the chance. Without further do, I clearly said that I would love the ability to take the right path at each decision point in my life. This is obviously an NP problem I'm facing with each day, since lots of variables are unknown, but there must be some sort of feasible decision to take. Should I just pick the best option I can and see what I can do with it or should I pick a random one and make the most out of it?",
    },
    {
      type: BlogPostContentType.TEXT_IT,
      content: 'What if we could always do the right things right?',
    },

    {
      type: BlogPostContentType.TEXT,
      content:
        'Over the years, life has taught me the hard lesson of patience. I was always impatiently waiting for results, but, "A watched pot never boils", right? I\'m learning every day that I should leave momentum do it\'s business, while I stay committed to doing the little things. Sometimes, things just take time. Letting go of full control and focusing on what you can actually influence pays off. Sometimes, you just have to embrace the dip knowing that in a while things will be better.',
    },
    {
      type: BlogPostContentType.SECTION_BREAK,
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'What if there would be some other way of planning the steps towards our goals? What if we could instead study consistently for 7 hours a day 6 days a week, do a 1-hour gym session 5 days a week and mindfully eat some chocolate once in a while. What if we could be more patient and wait for the magic of the compound effect to happen, silently adding the marginal gains every day, little by little. Things just take time.',
    },
    {
      type: BlogPostContentType.TEXT,
      content: 'Patiently, Mihai',
    },
  ],
};

export default CompoundingHabits;
